/**
 * State-card thumbnails — the Properties analog of keyframe-thumbnails.ts.
 *
 * Provides per-state preview images for the shared PoseStrip by posing the
 * owner's subtree off-screen (apply state → capture → restore, in one no-yield
 * window) — without disturbing the live selection.
 *
 * The freshness/capture CORE is shared (`trace-viewer/editor`):
 * `stateCardHash` — content hash of the subtree's authored appearance + the
 * per-state overrides across the subtree (descendant-override aware) — and
 * `enqueueStateCardCapture` — the serialized, deduped posed-capture queue
 * (one RTT camera at a time). This module keeps only the sandbox glue: the
 * PoseStrip provider shape, the dataURL cache, and the BEHAVIOR_CHANGED refresh.
 *
 * Exposes `makeStateThumbnailProvider(owner)` returning the `PoseThumbnailProvider`
 * the strip consumes (getThumbnail + subscribe). Capture is lazy: a card shows a
 * placeholder until its thumbnail lands.
 *
 * Properties is read LAZILY off the owner, so the Base card still captures for an
 * owner that has no Properties / no states yet — opening the panel shows a real
 * Base preview, not an empty placeholder.
 */

import type { SceneObject } from 'trace-viewer/scene';
import { stateCardHash, enqueueStateCardCapture } from 'trace-viewer/editor';
import { EDITOR_EVENTS, type BehaviorChangedDetail } from './editor-events';
import type { PoseThumbnailProvider } from './pose-strip';
import { ThumbnailCache, fileToDataUrl } from './thumbnail-cache';

const THUMB_SIZE = 192;
const BASE = '__base__';

/**
 * FIFO cap — same rationale as keyframe-thumbnails: cache keys embed transform
 * uniqueIds, new on every scene load, so without eviction dataURLs from prior
 * scenes stay pinned for the tab's lifetime.
 */
const MAX_CACHE_ENTRIES = 64;

// Module-level cache keyed by `${ownerUniqueId}:${stateName|BASE}`.
const cache = new ThumbnailCache(MAX_CACHE_ENTRIES);

function cacheKey(owner: SceneObject, name: string | null): string {
    return `${owner.transform.uniqueId}:${name ?? BASE}`;
}

/** The owner's state names, or [] when it has no Properties / no states yet. */
function ownerStateNames(owner: SceneObject): string[] {
    return owner.getBehavior('Properties')?.getStateNames() ?? [];
}

/** True when `node` is `ancestor` itself or anywhere below it in the hierarchy. */
function isSelfOrDescendant(node: SceneObject, ancestor: SceneObject): boolean {
    let cur: SceneObject | null = node;
    while (cur) {
        if (cur === ancestor) return true;
        cur = cur.getParent();
    }
    return false;
}

/**
 * Enqueue a card capture through the shared serialized queue. No-op if the
 * cached entry is fresh or the card is already queued. When the capture lands,
 * store the dataURL under the hash it depicts (computed pre-capture by the
 * shared core) and notify strip subscribers via the cache.
 */
function enqueueCapture(owner: SceneObject, name: string | null): void {
    const key = cacheKey(owner, name);
    enqueueStateCardCapture(owner, name, {
        size: THUMB_SIZE,
        isFresh: (hash) => cache.isFresh(key, hash),
        onCaptured: async ({ file, hash }) => {
            cache.set(key, { dataUrl: await fileToDataUrl(file), hash });
        },
    });
}

// One stable provider per owner uniqueId — re-fetched (not recreated) across
// React re-renders so we don't re-fire fills on every render.
const providers = new Map<number, PoseThumbnailProvider>();

/**
 * Build (or reuse) the PoseThumbnailProvider for an owner's state set.
 *
 * Properties is read lazily off the owner, so the Base card captures even when the
 * owner has no Properties / no states yet — the strip is never an empty placeholder
 * on open. `getThumbnail` returns the cached data URL (or undefined → placeholder)
 * and enqueues a serialized background capture when the card is missing/stale.
 */
export function makeStateThumbnailProvider(owner: SceneObject): PoseThumbnailProvider {
    const id = owner.transform.uniqueId;
    const existing = providers.get(id);
    // Reuse the stable provider without hashing every card: a shown card
    // re-checks its own freshness in getThumbnail.
    if (existing) return existing;

    /** Re-enqueue any stale/missing cards and notify subscribers. */
    const refresh = () => {
        for (const name of [null, ...ownerStateNames(owner)]) enqueueCapture(owner, name);
        cache.notify();
    };

    // A STATE write (e.g. setStateTransform → commitProps) changes states[].transform
    // but NOT authored data — so the Base subtree signature is unchanged and a
    // poll-only refresh would miss it. commitProps dispatches a Properties
    // BEHAVIOR_CHANGED; refresh when it targets the owner or a descendant so the
    // card recaptures (closing the asymmetry with Base writes, which DO change the
    // hashed authored signature). Behavior-agnostic to which descendant changed —
    // stateCardHash dedups, so a no-op change costs nothing. Resolve the changed
    // object once and walk UP to the owner (bounded by depth) instead of building
    // every descendant's path (bounded by breadth×depth) on every commit.
    const onBehaviorChanged = (event: Event) => {
        const detail = (event as CustomEvent<BehaviorChangedDetail>).detail;
        if (detail?.behaviorType !== 'Properties') return;
        const path = detail.objectPath;
        if (!path) return;
        const changed = owner.traceScene.resolveSceneObject(path);
        if (changed && isSelfOrDescendant(changed, owner)) refresh();
    };
    window.addEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, onBehaviorChanged);

    const provider: PoseThumbnailProvider = {
        getThumbnail(name: string | null): string | undefined {
            const key = cacheKey(owner, name);
            const entry = cache.get(key);
            if (!entry || entry.hash !== stateCardHash(owner, name)) {
                enqueueCapture(owner, name);
            }
            return entry?.dataUrl;
        },
        subscribe(callback: () => void): () => void {
            return cache.subscribe(callback);
        },
    };
    providers.set(id, provider);
    for (const name of [null, ...ownerStateNames(owner)]) enqueueCapture(owner, name);
    return provider;
}
